import { lazyWithRetry } from "@/utils/lazyWithRetry";
import giftClaimPromo from "@/assets/gift-claim-promo.webp";
import { AdminNotificationsDropdown } from "@/components/AdminNotificationsDropdown";
import { LoginModal } from "@/components/LoginModal";
import { RegisterModal } from "@/components/RegisterModal";
import { SupplierNotificationsDropdown } from "@/components/SupplierNotificationsDropdown";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuthContext } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useGroupedSupplierCategories } from "@/hooks/useSupplierCategories";
import { useSupplierPageId } from "@/hooks/useSupplierPageId";
import { debugSupabaseAuth } from "@/utils/authUtils";
import { getCategoryIcon, getCategoryLocationPath } from "@/utils/categoryPathMapping";
import { getCategoryPath as getCategoryPathById, getDisplayNameForDbCategory, CATEGORY_PATHS } from "@/config/categoryRoutes";
import { HeaderSearchBar } from "@/components/layout/HeaderSearchBar";
import { ChevronDown, ChevronLeft, ChevronRight, Menu, User } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import kyrkaIcon from '../../assets/icons/kyrka_ikon.webp';
import { FEATURED_CATEGORY_ICONS } from "@/config/featuredCategoryIcons";

// Lazy load the dropdown content - only loaded when user hovers on desktop
const NavigationDropdownContent = lazyWithRetry(() => import("./NavigationDropdownContent"));

// Loading skeleton for dropdown
const DropdownSkeleton = () => (
  <div className="flex gap-6 animate-pulse">
    <div className="flex-1">
      <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
      <div className="grid grid-cols-3 gap-4">
        {[...Array(9)].map((_, i) => (
          <div key={i} className="h-32 bg-gray-100 rounded-xl"></div>
        ))}
      </div>
    </div>
    <div className="w-px bg-gray-200"></div>
    <div className="flex-1">
      <div className="h-6 bg-gray-200 rounded w-64 mb-4"></div>
      <div className="grid grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
            <div className="h-3 bg-gray-100 rounded w-20"></div>
            <div className="h-3 bg-gray-100 rounded w-16"></div>
          </div>
        ))}
      </div>
    </div>
    <div className="w-px bg-gray-200"></div>
    <div className="w-80">
      <div className="h-44 bg-gray-100 rounded-xl"></div>
    </div>
  </div>
);


export const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [supplierMenuOpen, setSupplierMenuOpen] = useState(false);
  const [verktygMenuOpen, setVerktygMenuOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);

  const {
    data: groupedCategories,
    isLoading: categoriesLoading,
    error: categoriesError
  } = useGroupedSupplierCategories();

  const {
    user,
    userType,
    supplierProfile,
    signOut
  } = useAuthContext();

  // Check if user is admin
  const { isAdmin } = useAdminRole();

  // Get supplier page ID, slug, and category for venue link
  const { supplierPageId, supplierPageSlug, supplierPageCategory } = useSupplierPageId(supplierProfile?.id);

  // Supplier landing page: unique header, "Företagsartiklar" link, hide "För företag" CTA
  const isSupplierLandingPage = location.pathname.startsWith('/anslut-ditt-foretag');

  // Build supplier page URL
  const getSupplierPageUrl = () => {
    const slug = supplierPageSlug || supplierPageId;
    if (!slug) return null;
    const categoryPath = supplierPageCategory ? getCategoryPathById(supplierPageCategory) : null;
    return categoryPath ? `/${categoryPath}/leverantor/${slug}` : `/${CATEGORY_PATHS.VENUES}/leverantor/${slug}`;
  };

  // Safely flatten grouped categories for rendering
  const supplierCategories = groupedCategories
    ? Object.values(groupedCategories).flatMap(group => group?.subCategories || [])
    : [];

  // Transform supplier categories to include path and icon with null checks
  const categoriesWithPathAndIcon = supplierCategories
    ?.filter(category => category?.name)
    ?.map(category => ({
      name: category.name,
      path: getCategoryLocationPath(category.name),
      icon: getCategoryIcon(category.name)
    })) || [];

  const handleLoginClick = () => {
    setLoginModalOpen(true);
  };

  const handleRegisterClick = () => {
    setRegisterModalOpen(true);
  };

  const handleSupplierLinkClick = () => {};

  const handleLogout = async () => {
    await signOut();
  };

  const getUserDisplayName = () => {
    if (user?.user_metadata?.first_name) {
      return user.user_metadata.first_name;
    }
    return user?.email?.split('@')[0] || 'User';
  };

  const handleDebugAuth = async () => {
    await debugSupabaseAuth();
  };

  const handlePlannerClick = () => {
    if (user) {
      navigate('/planner');
    } else {
      setLoginModalOpen(true);
    }
  };

  const handlePlannerClickMobile = () => {
    if (user) {
      navigate('/planner');
      setMobileMenuOpen(false);
    } else {
      setMobileMenuOpen(false);
      setLoginModalOpen(true);
    }
  };

  const [openDropdownMenu, setOpenDropdownMenu] = useState(false);
  const supplierDropdownCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [verktygDropdownOpen, setVerktygDropdownOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (supplierDropdownCloseTimeoutRef.current) clearTimeout(supplierDropdownCloseTimeoutRef.current);
    };
  }, []);

  const VERKTYG_LINKS = [
    { title: "Checklista", link: "/verktyg/brollop-checklista-pdf" },
    { title: "Bordsplacering", link: "/verktyg/bordsplacering-mall" },
    { title: "Gästlista", link: "/verktyg/gastlista-mall" },
    { title: "Bröllopsbudget", link: "/verktyg/brollopsbudget-kalkylator" },
    { title: "Körschema", link: "/verktyg/tidslinje-brollopsdagen" },
    { title: "Sparade favoriter", link: "/verktyg/sparade-brollopsfavoriter" },
  ];

  // Desktop dropdown uses lazy-loaded component

  return (
    <>
      {/* Navigation */}
      <nav className={`w-full flex justify-between items-center border-b shadow-sm py-2.5 px-4 sm:px-8 lg:px-8 xl:px-[146px] transition-all duration-300 ${isSupplierLandingPage ? 'bg-white border-gray-200' : 'backdrop-blur-md bg-white/98 border-gray-100/80'}`} style={isSupplierLandingPage ? undefined : {
        background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.98) 100%)',
        borderBottom: '1px solid hsl(var(--border) / 0.12)'
      }}>
        {/* Mobile/Tablet Header */}
        <div className="lg:hidden flex items-center justify-between w-full">
          {/* Hamburger menu - left */}
          <button
            className="text-gray-800 p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Stäng meny" : "Öppna meny"}
            aria-expanded={mobileMenuOpen}
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Logo - center */}
          <Link to="/" className="flex-1 flex justify-center">
            <img src="/lovable-uploads/weddingly-logo.webp" className="w-[145px] h-[42px] sm:w-[194px] sm:h-[56px]" alt="Weddingly - Bröllopsplanering" width="194" height="56" />
          </Link>

          {/* Icons - right */}
          <div className="flex items-center gap-1">
            {/* Admin notification bell - mobile */}
            {isAdmin && <AdminNotificationsDropdown />}

            {/* Supplier notification bell - mobile */}
            {!isAdmin && userType === 'supplier' && supplierProfile && (
              <SupplierNotificationsDropdown supplierId={supplierProfile.id} />
            )}

            {/* User dropdown */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="text-gray-800 p-2"
                  aria-label="Användarmeny"
                >
                  <User className="w-5 h-5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 bg-white border border-gray-100/50 shadow-lg rounded-lg z-50 p-2 mt-2 mr-4">
                  {userType === 'supplier' && supplierProfile && (
                    <DropdownMenuItem asChild>
                      <Link to={`/supplier/${supplierProfile.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                        Min Leverantörsprofil
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {userType === 'supplier' ? (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/supplier-conversations" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                          Mina förfrågningar
                        </Link>
                      </DropdownMenuItem>
                      {supplierPageId && getSupplierPageUrl() && (
                        <DropdownMenuItem asChild>
                          <Link to={getSupplierPageUrl()!} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                            Visa min profil
                          </Link>
                        </DropdownMenuItem>
                      )}
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/my-conversations" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                          Mina samtal
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  {userType !== 'supplier' && (
                    <DropdownMenuItem asChild>
                      <Link to="/profile-settings" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                        Profilinställningar
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <button onClick={handleLogout} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                      Logga ut
                    </button>
                  </DropdownMenuItem>
                  {userType !== 'supplier' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/planner/checklist" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                          Checklista
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/planner/guestlist" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                          Gästlista
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/planner/budget" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                          Budget
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="text-gray-800 p-2"
                  aria-label="Logga in eller registrera"
                >
                  <User className="w-5 h-5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 bg-white border border-gray-100/50 shadow-lg rounded-lg z-50 p-2 mt-2 mr-4 flex flex-col items-center">
                  <DropdownMenuItem asChild className="flex justify-center !rounded-full p-0">
                    <button onClick={handleLoginClick} className="w-full text-center px-4 py-2 text-sm font-normal text-gray-800 hover:bg-gray-50 !rounded-full border border-gray-800 transition-colors">
                      Logga in
                    </button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="flex justify-center">
                    <button onClick={handleRegisterClick} className="w-full text-center px-3 py-2 text-sm hover:bg-gray-50 rounded">
                      Registrera
                    </button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Desktop Header - three-zone flex so center shrinks and never overlaps right */}
        <div className="hidden lg:flex items-center w-full gap-2">
          {/* Left: Logo */}
          <Link to="/" className="mr-4 xl:mr-8 flex items-center shrink-0" aria-label="Weddingly startsida">
            <img
              src="/lovable-uploads/weddingly-logo.webp"
              className="w-[194px] h-[56px]"
              alt="Weddingly - Planera ert bröllop enkelt"
              width="194"
              height="56"
              {...({ fetchpriority: "high" } as any)}
            />
          </Link>

          {/* Center: Nav links - flex-1 so it takes remaining space and shrinks before overlapping */}
          <div className="flex-1 min-w-0 flex justify-center items-center gap-4 xl:gap-6 overflow-hidden">
            <div
              onMouseEnter={() => {
                if (supplierDropdownCloseTimeoutRef.current) {
                  clearTimeout(supplierDropdownCloseTimeoutRef.current);
                  supplierDropdownCloseTimeoutRef.current = null;
                }
                setOpenDropdownMenu(true);
              }}
              onMouseLeave={() => {
                if (supplierDropdownCloseTimeoutRef.current) clearTimeout(supplierDropdownCloseTimeoutRef.current);
                supplierDropdownCloseTimeoutRef.current = setTimeout(() => {
                  setOpenDropdownMenu(false);
                  supplierDropdownCloseTimeoutRef.current = null;
                }, 200);
              }}
              className="relative h-full"
            >
              <DropdownMenu
                open={openDropdownMenu}
                onOpenChange={(open) => {
                  if (!open && supplierDropdownCloseTimeoutRef.current) {
                    clearTimeout(supplierDropdownCloseTimeoutRef.current);
                    supplierDropdownCloseTimeoutRef.current = null;
                  }
                  setOpenDropdownMenu(open);
                }}
                modal={false}
              >
                <DropdownMenuTrigger
                  className="asChild h-full flex items-center gap-1 text-[hsl(var(--link-blue))] text-sm font-bold hover:text-[hsl(var(--link-blue))]/80 transition-colors"
                  aria-label="Visa alla leverantörer"
                  aria-expanded={openDropdownMenu}
                >
                  Alla Leverantörer
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <div className="absolute left-0 top-full w-full h-3 z-40 pointer-events-auto"
                // Invisible bridge to avoid open and closing the dropdownmenu when the mouse lefts the trigger and entering the dropdownmenu
                >
                  {openDropdownMenu && (
                    <DropdownMenuContent
                      align="center"
                      className="w-[calc(100vw-80px)] max-w-[1400px] bg-white border border-gray-100/50 shadow-xl rounded-2xl z-50 p-6 mt-2 will-change-transform"
                      style={{
                        background: 'rgba(255, 255, 255, 0.98)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                        transform: 'translateZ(0)',
                      }}
                      onMouseEnter={() => {
                        if (supplierDropdownCloseTimeoutRef.current) {
                          clearTimeout(supplierDropdownCloseTimeoutRef.current);
                          supplierDropdownCloseTimeoutRef.current = null;
                        }
                        setOpenDropdownMenu(true);
                      }}
                      onMouseLeave={() => {
                        if (supplierDropdownCloseTimeoutRef.current) clearTimeout(supplierDropdownCloseTimeoutRef.current);
                        setOpenDropdownMenu(false);
                      }}
                    >
                      <Suspense fallback={<DropdownSkeleton />}>
                        <NavigationDropdownContent />
                      </Suspense>
                    </DropdownMenuContent>
                  )}
                </div>
              </DropdownMenu>
            </div>

            {user ? (
              <button
                onClick={handlePlannerClick}
                className="text-[hsl(var(--link-blue))] text-sm font-normal hover:text-[hsl(var(--link-blue))]/80 transition-colors cursor-pointer bg-transparent border-none"
                aria-label="Gå till checklista-appen"
              >
                Planeringsverktyget
              </button>
            ) : (
              <DropdownMenu open={verktygDropdownOpen} onOpenChange={setVerktygDropdownOpen} modal={false}>
                <div
                  onMouseEnter={() => setVerktygDropdownOpen(true)}
                  onMouseLeave={() => setVerktygDropdownOpen(false)}
                  className="relative h-full"
                >
                  <DropdownMenuTrigger
                    className="h-full flex items-center gap-1 text-[hsl(var(--link-blue))] text-sm font-normal hover:text-[hsl(var(--link-blue))]/80 transition-colors cursor-pointer bg-transparent border-none"
                    onClick={(e) => {
                      if (!verktygDropdownOpen) {
                        navigate('/verktyg');
                      }
                    }}
                    aria-label="Visa verktyg och mallar"
                    aria-expanded={verktygDropdownOpen}
                  >
                    Verktyg & mallar
                    <ChevronDown className="w-3 h-3" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    sideOffset={8}
                    className="w-56 bg-white border border-gray-100/50 shadow-lg rounded-lg z-50 p-2"
                    onMouseEnter={() => setVerktygDropdownOpen(true)}
                    onMouseLeave={() => setVerktygDropdownOpen(false)}
                  >
                    {VERKTYG_LINKS.map((item) => (
                      <DropdownMenuItem key={item.link} asChild>
                        <Link
                          to={item.link}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded"
                        >
                          {item.title}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </div>
              </DropdownMenu>
            )}
            {isSupplierLandingPage ? (
              <Link to="/anslut-ditt-foretag/foretagsartiklar" className="text-[hsl(var(--link-blue))] text-sm font-normal hover:text-[hsl(var(--link-blue))]/80 transition-colors">
                Företagsartiklar
              </Link>
            ) : (
              <Link to="/inspiration" className="text-[hsl(var(--link-blue))] text-sm font-normal hover:text-[hsl(var(--link-blue))]/80 transition-colors">
                Inspiration & guider
              </Link>
            )}
          </div>

          {/* Right: Search, notifications, auth - shrink-0 so it never shrinks */}
          <div className="shrink-0 flex items-center gap-3 xl:gap-4">
            <HeaderSearchBar className="shrink-0" />

            {/* Admin Notification Bell - Only for admins on desktop */}
            {isAdmin && (
              <div className="hidden lg:block">
                <AdminNotificationsDropdown />
              </div>
            )}

            {/* Supplier Notification Bell - Only for suppliers on desktop */}
            {userType === 'supplier' && supplierProfile && (
              <div className="hidden lg:block">
                <SupplierNotificationsDropdown supplierId={supplierProfile.id} />
              </div>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="flex items-center gap-2 blue-button border border-white rounded-full"
                  aria-label="Användarmeny"
                >
                  {getUserDisplayName()}
                  <ChevronDown className="w-4 h-4 text-white" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 bg-white border border-gray-100/50 shadow-lg rounded-lg z-50 p-2 mt-2">
                  {userType === 'supplier' && supplierProfile && (
                    <DropdownMenuItem asChild>
                      <Link to={`/supplier/${supplierProfile.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                        Min Leverantörsprofil
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {userType === 'supplier' ? (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/supplier-conversations" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                          Mina förfrågningar
                        </Link>
                      </DropdownMenuItem>
                      {supplierPageId && getSupplierPageUrl() && (
                        <DropdownMenuItem asChild>
                          <Link to={getSupplierPageUrl()!} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                            Visa min sida
                          </Link>
                        </DropdownMenuItem>
                      )}
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/my-conversations" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                          Mina samtal
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  {userType !== 'supplier' && (
                    <DropdownMenuItem asChild>
                      <Link to="/profile-settings" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                        Profilinställningar
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <button onClick={handleLogout} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                      Logga ut
                    </button>
                  </DropdownMenuItem>
                  {userType !== 'supplier' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/planner/checklist" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                          Checklista
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/planner/guestlist" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                          Gästlista
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/planner/budget" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                          Budget
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleLoginClick}
                  className="border border-gray-800 rounded-full text-dark-blue bg-transparent px-4 py-2 hover:bg-gray-50 transition-colors text-sm font-normal"
                >
                  Logga in
                </button>
                {!user && !isSupplierLandingPage && (
                  <Link
                    to="/anslut-ditt-foretag"
                    className="blue-button rounded-full font-bold"
                  >
                    För företag
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile menu backdrop and overlay */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Mobile menu */}
          <div className="lg:hidden fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 transform transition-transform duration-300 ease-out animate-slide-in-left">
            <div className="h-full overflow-y-auto">
              <div className="flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                    <img src="/lovable-uploads/weddingly-logo.webp" className="w-[138px] h-[40px]" alt="Weddingly Logo" width="138" height="40" loading="lazy" />
                  </Link>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 p-6 space-y-6">
                  <HeaderSearchBar className="w-full [&_input]:max-w-none [&_input]:min-w-0" />
                  {/* Main Navigation */}
                  <div className="space-y-0">
                    <button className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 transition-colors min-h-[44px] flex items-center justify-between w-full text-left border-b border-gray-200" onClick={() => setSupplierMenuOpen(true)}>
                      <span>Leverantörer</span>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                    {user ? (
                      <button onClick={handlePlannerClickMobile} className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 transition-colors min-h-[44px] flex items-center justify-between border-b border-gray-200 w-full text-left">
                        <span>Planeringsverktyget</span>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                    ) : (
                      <button onClick={() => setVerktygMenuOpen(true)} className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 transition-colors min-h-[44px] flex items-center justify-between border-b border-gray-200 w-full text-left">
                        <span>Verktyg & mallar</span>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                    )}
                    {isSupplierLandingPage ? (
                      <Link to="/anslut-ditt-foretag/foretagsartiklar" className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 transition-colors min-h-[44px] flex items-center justify-between border-b border-gray-200" onClick={() => setMobileMenuOpen(false)}>
                        <span>Företagsartiklar</span>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </Link>
                    ) : (
                      <Link to="/inspiration" className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 transition-colors min-h-[44px] flex items-center justify-between border-b border-gray-200" onClick={() => setMobileMenuOpen(false)}>
                        <span>Inspiration & guider</span>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </Link>
                    )}
                    {!user && !isSupplierLandingPage && (
                      <Link to="/anslut-ditt-foretag" onClick={() => {
                        handleSupplierLinkClick();
                        setMobileMenuOpen(false);
                      }} className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 transition-colors min-h-[44px] flex items-center justify-between border-b border-gray-200">
                        <span className="font-semibold">För leverantörer</span>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </Link>
                    )}
                  </div>

                  {/* User section */}
                  <div className="border-t border-gray-100 pt-6">
                    {user ? (
                      <div className="space-y-3">
                        <div className="text-dark-blue text-sm font-semibold mb-4">
                          Inloggad som {getUserDisplayName()}
                        </div>
                        {userType === 'supplier' && supplierProfile && (
                          <Link to={`/supplier/${supplierProfile.id}`} className="block text-gray-700 text-base py-3 px-3 -mx-3 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] flex items-center" onClick={() => setMobileMenuOpen(false)}>
                            Min Leverantörsprofil
                          </Link>
                        )}
                        {userType === 'supplier' ? (
                          <>
                            <Link to="/supplier-conversations" className="block text-gray-700 text-base py-3 px-3 -mx-3 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] flex items-center" onClick={() => setMobileMenuOpen(false)}>
                              Mina förfrågningar
                            </Link>
                            {supplierPageId && getSupplierPageUrl() && (
                              <Link to={getSupplierPageUrl()!} className="block text-gray-700 text-base py-3 px-3 -mx-3 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] flex items-center" onClick={() => setMobileMenuOpen(false)}>
                                Visa min profil
                              </Link>
                            )}
                          </>
                        ) : (
                          <Link to="/my-conversations" className="block text-gray-700 text-base py-3 px-3 -mx-3 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] flex items-center" onClick={() => setMobileMenuOpen(false)}>
                            Mina samtal
                          </Link>
                        )}
                        {userType !== 'supplier' && (
                          <Link to="/profile-settings" className="block text-gray-700 text-base py-3 px-3 -mx-3 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] flex items-center" onClick={() => setMobileMenuOpen(false)}>
                            Profilinställningar
                          </Link>
                        )}
                        <button onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }} className="block w-full text-left text-red-600 text-base py-3 px-3 -mx-3 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] flex items-center">
                          Logga ut
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <button onClick={() => {
                          handleLoginClick();
                          setMobileMenuOpen(false);
                        }} className="w-full border border-gray-800 text-gray-800 text-base py-3 px-6 rounded-full hover:bg-gray-50 transition-colors min-h-[44px] flex items-center justify-center">
                          Logga in
                        </button>
                        <button onClick={() => {
                          handleRegisterClick();
                          setMobileMenuOpen(false);
                        }} className="w-full blue-button rounded-full font-bold text-base py-3 px-6 min-h-[44px] flex items-center justify-center">
                          Skapa konto
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile Supplier Categories Submenu */}
      {supplierMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in"
            onClick={() => setSupplierMenuOpen(false)}
          />

          {/* Supplier submenu */}
          <div className="lg:hidden fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 transform transition-transform duration-300 ease-out animate-slide-in-left">
            <div className="h-full overflow-y-auto">
              <div className="flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSupplierMenuOpen(false)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <h2 className="text-lg font-semibold text-dark-blue">Leverantörer</h2>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 p-6 space-y-8">
                  {/* Featured Categories Section */}
                  <div>
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-dark-blue mb-2">Börja din sökning här</h3>
                      <p className="text-sm text-gray-600">Våra mest populära kategorier</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {categoriesLoading ? (
                        <div className="col-span-2 text-center text-gray-500 py-4">
                          Laddar kategorier...
                        </div>
                      ) : categoriesError ? (
                        <div className="col-span-2 text-center text-gray-500 py-4">
                          Kunde inte ladda kategorier
                        </div>
                      ) : (
                        categoriesWithPathAndIcon
                          .filter(item => ['Bröllopslokaler', 'Bröllopskostym', 'Brudklänning', 'Bröllopsfotograf', 'Videograf', 'Vigselringar', 'Bröllopstårta', 'Brudbukett', 'Bröllopskort'].includes(item.name))
                          .map(item => (
                            <Link
                              key={item.name}
                              to={item.path}
                              onClick={() => {
                                setSupplierMenuOpen(false);
                                setMobileMenuOpen(false);
                              }}
                              className="flex flex-col items-center justify-between text-center p-4 rounded-xl hover:bg-white/80 hover:shadow-md transition-all duration-200 group border border-gray-100/60 hover:border-gray-200/80 bg-white/30 h-32"
                            >
                              <div className="text-2xl group-hover:scale-110 transition-transform duration-200 flex items-center justify-center">
                                <img
                                  src={FEATURED_CATEGORY_ICONS[item.name]}
                                  alt={`${getDisplayNameForDbCategory(item.name)} icon`}
                                  className="w-14 h-14 object-contain"
                                />
                              </div>
                              <span className="text-xs font-medium text-gray-700 group-hover:text-dark-blue transition-colors leading-tight">
                                {getDisplayNameForDbCategory(item.name)}
                              </span>
                            </Link>
                          ))
                      )}
                      {/* Static Kyrkor button */}
                      {!categoriesLoading && !categoriesError && (
                        <Link
                          to="/kyrkor"
                          onClick={() => {
                            setSupplierMenuOpen(false);
                            setMobileMenuOpen(false);
                          }}
                          className="flex flex-col items-center justify-between text-center p-4 rounded-xl hover:bg-white/80 hover:shadow-md transition-all duration-200 group border border-gray-100/60 hover:border-gray-200/80 bg-white/30 h-32"
                        >
                          <div className="text-2xl group-hover:scale-110 transition-transform duration-200 flex items-center justify-center">
                            <img src={kyrkaIcon} alt="Kyrkor" className="w-14 h-14 object-contain" />
                          </div>
                          <span className="text-xs font-medium text-gray-700 group-hover:text-dark-blue transition-colors leading-tight">
                            Kyrkor
                          </span>
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* All Categories Section */}
                  <div>
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-dark-blue mb-2">Hitta alla leverantörer för ett komplett bröllop</h3>
                    </div>
                    <div className="space-y-6">
                      {categoriesLoading ? (
                        <div className="text-center text-gray-500 py-4">
                          Laddar kategorier...
                        </div>
                      ) : categoriesError ? (
                        <div className="text-center text-gray-500 py-4">
                          Kunde inte ladda kategorier
                        </div>
                      ) : (
                        Object.entries(groupedCategories || {}).map(([groupName, group]) => {
                          if (!group || !group.subCategories) return null;

                          return (
                            <div key={groupName} className="space-y-3">
                              <h4 className="text-sm font-medium text-dark-blue border-b border-gray-200 pb-2">
                                {group.mainCategory?.name || groupName}
                              </h4>
                              <div className="grid grid-cols-2 gap-2">
                                {group.subCategories
                                  ?.filter(category => category?.name)
                                  ?.map(category => {
                                    const categoryWithPath = categoriesWithPathAndIcon.find(c => c.name === category.name);
                                    return categoryWithPath ? (
                                      <Link
                                        key={category.name}
                                        to={categoryWithPath.path}
                                        onClick={() => {
                                          setSupplierMenuOpen(false);
                                          setMobileMenuOpen(false);
                                        }}
                                        className="block text-sm text-gray-700 py-2 px-2 hover:bg-gray-50 rounded-lg transition-colors"
                                      >
                                        {category.name}
                                      </Link>
                                    ) : null;
                                  })}
                              </div>
                            </div>
                          );
                        }).filter(Boolean)
                      )}
                    </div>
                  </div>

                  {/* Gift Claim Promotional Section */}
                  <div>
                    <Link
                      to="/gift/claim"
                      onClick={() => {
                        setSupplierMenuOpen(false);
                        setMobileMenuOpen(false);
                      }}
                      className="block relative group overflow-hidden rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-300"
                    >
                      <div className="relative h-32">
                        <img
                          src={giftClaimPromo}
                          alt="Wedding Gift"
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                        <div className="absolute inset-0 flex items-center justify-center p-4">
                          <h3 className="text-white text-base font-semibold leading-tight text-center">
                            Hittat din lokal? Hämta din gåva
                          </h3>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile Verktyg & mallar Submenu */}
      {verktygMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in"
            onClick={() => setVerktygMenuOpen(false)}
          />

          {/* Verktyg submenu */}
          <div className="lg:hidden fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 transform transition-transform duration-300 ease-out animate-slide-in-left">
            <div className="h-full overflow-y-auto">
              <div className="flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setVerktygMenuOpen(false)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <h2 className="text-lg font-semibold text-dark-blue">Verktyg & mallar</h2>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 p-6 space-y-2">
                  <Link
                    to="/verktyg/brollop-checklista-pdf"
                    onClick={() => {
                      setVerktygMenuOpen(false);
                      setMobileMenuOpen(false);
                    }}
                    className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    Checklista
                  </Link>
                  <Link
                    to="/verktyg/bordsplacering-mall"
                    onClick={() => {
                      setVerktygMenuOpen(false);
                      setMobileMenuOpen(false);
                    }}
                    className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    Bordsplacering
                  </Link>
                  <Link
                    to="/verktyg/gastlista-mall"
                    onClick={() => {
                      setVerktygMenuOpen(false);
                      setMobileMenuOpen(false);
                    }}
                    className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    Gästlista
                  </Link>
                  <Link
                    to="/verktyg/brollopsbudget-kalkylator"
                    onClick={() => {
                      setVerktygMenuOpen(false);
                      setMobileMenuOpen(false);
                    }}
                    className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    Bröllopsbudget
                  </Link>
                  <Link
                    to="/verktyg/tidslinje-brollopsdagen"
                    onClick={() => {
                      setVerktygMenuOpen(false);
                      setMobileMenuOpen(false);
                    }}
                    className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    Körschema
                  </Link>
                  <Link
                    to="/verktyg/sparade-brollopsfavoriter"
                    onClick={() => {
                      setVerktygMenuOpen(false);
                      setMobileMenuOpen(false);
                    }}
                    className="block text-gray-700 text-base py-3 px-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    Sparade favoriter
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Login and Register Modals */}
      <LoginModal
        open={loginModalOpen}
        onOpenChange={setLoginModalOpen}
        onRegisterClick={() => setRegisterModalOpen(true)}
      />
      <RegisterModal
        open={registerModalOpen}
        onOpenChange={setRegisterModalOpen}
        onLoginClick={() => setLoginModalOpen(true)}
      />
    </>
  );
};
